import {functions, httpsCallable} from "@/firebase/firebaseConfig";
import {extractAffiliateRefCodeFromLink} from "@/lib/AffiliateLink";
import {
    getLocalStorageItem,
    getSessionStorageItem,
    removeLocalStorageItem,
    removeSessionStorageItem,
    setLocalStorageItem,
    setSessionStorageItem,
} from "@/lib/storageUtils";

export type PublicClaimSourceContext = "public_optimize";

export interface PublicVideoClaimContext {
    claimToken: string;
    resolvedRefCode?: string;
    refSource?: "owner_affiliate" | "client_hint" | "none";
    sourceUserVideoId: string;
    sourceContext: PublicClaimSourceContext;
    expiresAt: string;
    createdAt: string;
}

export interface CreatePublicClaimIntentInput {
    sourceUserVideoId: string;
    affiliateRefCode?: string;
    sourceContext?: PublicClaimSourceContext;
}

export interface CreatePublicClaimIntentResponse {
    claimToken: string;
    expiresAt: string;
    resolvedRefCode?: string;
    sourceCreatorUid?: string;
    refSource?: "owner_affiliate" | "client_hint" | "none";
}

export interface RedeemPublicClaimIntentInput {
    claimToken: string;
}

export interface RedeemPublicClaimIntentResponse {
    success: boolean;
    userVideoId: string;
}

const CLAIM_CONTEXT_STORAGE_KEY = "publicVideoClaimContext";
const CLAIM_BACKGROUND_REDEEM_PREFIX = "publicVideoClaimBackgroundRedeem:";
const CLAIM_BACKGROUND_REDEEM_RESULT_KEY = "publicVideoClaimBackgroundRedeemResult";
const APP_BASE_URL = (process.env.NEXT_PUBLIC_APP_BASE_URL || "https://app.braiv.co").replace(/\/+$/, "");
const SUPPORTED_CLAIM_SOURCE_CONTEXT: PublicClaimSourceContext = "public_optimize";

export const createPublicClaimIntent = async (
    input: CreatePublicClaimIntentInput
): Promise<CreatePublicClaimIntentResponse> => {
    const fn = httpsCallable<CreatePublicClaimIntentInput, CreatePublicClaimIntentResponse>(
        functions,
        "video-createPublicClaimIntent"
    );
    const result = await fn(input);
    return result.data;
};

export const redeemPublicClaimIntent = async (
    input: RedeemPublicClaimIntentInput
): Promise<RedeemPublicClaimIntentResponse> => {
    const fn = httpsCallable<RedeemPublicClaimIntentInput, RedeemPublicClaimIntentResponse>(
        functions,
        "video-redeemPublicClaimIntent"
    );
    const result = await fn(input);
    return result.data;
};

export const savePublicVideoClaimContext = (context: PublicVideoClaimContext): void => {
    setLocalStorageItem(CLAIM_CONTEXT_STORAGE_KEY, JSON.stringify(context));
};

export const readPublicVideoClaimContext = (): PublicVideoClaimContext | null => {
    try {
        const raw = getLocalStorageItem(CLAIM_CONTEXT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as PublicVideoClaimContext;
        if (!parsed?.claimToken || !parsed?.sourceUserVideoId || !parsed?.sourceContext) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const isSupportedClaimSource = (source?: string): source is PublicClaimSourceContext =>
    source === SUPPORTED_CLAIM_SOURCE_CONTEXT;

const isValidStoredClaimContext = (context: PublicVideoClaimContext | null): context is PublicVideoClaimContext => {
    if (!context?.claimToken || !context?.sourceUserVideoId || !isSupportedClaimSource(context?.sourceContext)) {
        return false;
    }
    if (claimLooksExpired(context.expiresAt)) {
        return false;
    }
    return true;
};

export const clearPublicVideoClaimContext = (): void => {
    removeLocalStorageItem(CLAIM_CONTEXT_STORAGE_KEY);
};

export const parseRefCodeFromUrl = (url: string): string | undefined => {
    return extractAffiliateRefCodeFromLink(url) || undefined;
};

export const parseRefCodeFromCurrentLocation = (): string | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }
    const ref = new URLSearchParams(window.location.search).get("ref");
    return ref || undefined;
};

export const buildPublicClaimSignupUrl = (params: {
    claimToken: string;
    sourceUserVideoId: string;
    resolvedRefCode?: string;
    sourceContext?: PublicClaimSourceContext;
    absolute?: boolean;
}): string => {
    const sourceContext = params.sourceContext || "public_optimize";
    const query = new URLSearchParams();
    query.set("claim", params.claimToken);
    query.set("src", sourceContext);
    if (params.resolvedRefCode) {
        query.set("ref", params.resolvedRefCode);
    }

    // Analytics-only fields
    query.set("vid", params.sourceUserVideoId);
    query.set("utm_source", "public_video");
    query.set("utm_campaign", "optimize_cta");

    const path = `/signup?${query.toString()}`;
    return params.absolute === false ? path : `${APP_BASE_URL}${path}`;
};

export const buildPublicClaimLoginUrl = (params: {
    claimToken: string;
    sourceUserVideoId: string;
    resolvedRefCode?: string;
    sourceContext?: PublicClaimSourceContext;
    absolute?: boolean;
}): string => {
    const sourceContext = params.sourceContext || "public_optimize";
    const query = new URLSearchParams();
    query.set("claim", params.claimToken);
    query.set("src", sourceContext);
    if (params.resolvedRefCode) {
        query.set("ref", params.resolvedRefCode);
    }

    // Analytics-only fields
    query.set("vid", params.sourceUserVideoId);
    query.set("utm_source", "public_video");
    query.set("utm_campaign", "optimize_cta");

    const path = `/login?${query.toString()}`;
    return params.absolute === false ? path : `${APP_BASE_URL}${path}`;
};

export const buildSignupClaimUrl = (params: {
    claimToken: string;
    sourceUserVideoId: string;
    ref?: string;
    sourceContext?: PublicClaimSourceContext;
}): string => {
    return buildPublicClaimSignupUrl({
        claimToken: params.claimToken,
        sourceUserVideoId: params.sourceUserVideoId,
        resolvedRefCode: params.ref,
        sourceContext: params.sourceContext,
        absolute: false,
    });
};

export const buildClaimResolverUrl = (params: {
    claimToken: string;
    sourceUserVideoId: string;
    ref?: string;
    sourceContext?: PublicClaimSourceContext;
}): string => {
    const sourceContext = params.sourceContext || "public_optimize";
    const query = new URLSearchParams();
    query.set("claim", params.claimToken);
    query.set("src", sourceContext);
    if (params.ref) {
        query.set("ref", params.ref);
    }
    query.set("vid", params.sourceUserVideoId);
    query.set("utm_source", "public_video");
    query.set("utm_campaign", "optimize_cta");
    return `/welcome/claim?${query.toString()}`;
};

export const hasClaimInCurrentLocation = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }
    const params = new URLSearchParams(window.location.search);
    const urlClaim = params.get("claim");
    const urlSource = params.get("src");
    if (urlClaim && (!urlSource || isSupportedClaimSource(urlSource))) {
        return true;
    }

    const stored = readPublicVideoClaimContext();
    if (!isValidStoredClaimContext(stored)) {
        clearPublicVideoClaimContext();
        return false;
    }
    return true;
};

export const buildClaimResolverUrlFromCurrentLocation = (): string => {
    if (typeof window === "undefined") {
        return "/welcome/claim";
    }

    const current = new URLSearchParams(window.location.search);
    const stored = readPublicVideoClaimContext();
    const passthrough = new URLSearchParams();
    for (const key of ["claim", "ref", "src", "vid", "utm_source", "utm_campaign"]) {
        const value = current.get(key);
        if (value) {
            passthrough.set(key, value);
        }
    }
    if (!passthrough.get("claim") && stored?.claimToken) {
        passthrough.set("claim", stored.claimToken);
    }
    if (!passthrough.get("src") && stored?.sourceContext) {
        passthrough.set("src", stored.sourceContext);
    }
    if (!passthrough.get("vid") && stored?.sourceUserVideoId) {
        passthrough.set("vid", stored.sourceUserVideoId);
    }
    if (!passthrough.get("ref") && stored?.resolvedRefCode) {
        passthrough.set("ref", stored.resolvedRefCode);
    }

    const query = passthrough.toString();
    return query ? `/welcome/claim?${query}` : "/welcome/claim";
};

export const claimLooksExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) {
        return false;
    }
    const parsed = Date.parse(expiresAt);
    if (Number.isNaN(parsed)) {
        return false;
    }
    return Date.now() > parsed;
};

export const startBackgroundClaimRedeem = async (): Promise<boolean> => {
    if (typeof window === "undefined") {
        return false;
    }

    const params = new URLSearchParams(window.location.search);
    const stored = readPublicVideoClaimContext();
    const claimToken = params.get("claim") || stored?.claimToken;
    const sourceContext = params.get("src") || stored?.sourceContext;

    if (!claimToken) {
        return false;
    }

    if (!isSupportedClaimSource(sourceContext || undefined)) {
        // Prevent background redeem from running outside supported claim flows.
        return false;
    }

    if (claimLooksExpired(stored?.expiresAt)) {
        clearPublicVideoClaimContext();
        return false;
    }

    const dedupeKey = `${CLAIM_BACKGROUND_REDEEM_PREFIX}${claimToken}`;
    const currentState = getSessionStorageItem(dedupeKey);
    if (currentState === "inflight" || currentState === "done") {
        return false;
    }

    setSessionStorageItem(dedupeKey, "inflight");
    try {
        const response = await redeemPublicClaimIntent({claimToken});
        clearPublicVideoClaimContext();
        setSessionStorageItem(dedupeKey, "done");
        setLocalStorageItem(
            CLAIM_BACKGROUND_REDEEM_RESULT_KEY,
            JSON.stringify({
                userVideoId: response.userVideoId,
                completedAt: new Date().toISOString(),
            })
        );
        return true;
    } catch (error) {
        removeSessionStorageItem(dedupeKey);
        console.error("[PublicClaim] Background redeem failed", error);
        return false;
    }
};
